import { defineFeature, defineModule, definePermission } from '@shared/contract'
import { STOCK_FEATURE } from '@shared/stock'

// Bestand: Übersicht je Artikel und Standort, Bewegungsjournal, Chargen und MHD, Wareneingang, Umlagerung über den
// Transit, Korrekturbuchungen mit Freigabe.
//
// Rechte – alle je Standort vergebbar (db/seeds/demo/roles.ts, Abschnitt „Bestand“):
//   * Lesen je Gegenstand; Einstandspreise und Lagerwerte zusätzlich nur mit `costs.read`.
//   * Umlagerung: anfordern (am empfangenden ODER abgebenden Standort), freigeben und versenden (am abgebenden),
//     empfangen (am empfangenden). Gelesen wird eine Umlagerung an beiden Enden.
//   * Grenzen: `maxAdjustmentCents` (Wert einer Korrektur zum gleitenden Durchschnitt) und `maxReceiptCents`
//     (Warenwert eines Wareneingangs). Über der Grenze wartet der Beleg auf Freigabe.
const GROUP = 'Lager'

export default defineModule({
  id: 'inventory',
  title: 'Bestand',
  description: 'Bestandsübersicht, Bewegungsjournal, Wareneingang, Umlagerung, Korrekturbuchungen – mit der Funktion dazu auch Rückverfolgung nach Warenposten.',
  dependsOn: ['platform', 'masterdata'],
  features: [
    // Bestandsführung (docs/ohne-lager/SPEZIFIKATION.md): Vorgabe an. Aus blendet die Oberfläche von Lager, Einkauf und
    // Inventur aus (feature: 'stock' in den Manifesten) und lässt Artikel als nicht bestandsgeführt gelten – außer
    // Leihartikeln (ledger.isStockManaged). Die Aktionen bleiben aufrufbar: Der Verleih pflegt seinen Bestand weiter.
    defineFeature({
      key: STOCK_FEATURE,
      label: 'Bestandsführung',
      description: 'Bestände je Standort führen: Lager mit Wareneingang, Umlagerung und Korrektur, dazu Einkauf mit Bestellvorschlag und Inventur.',
      defaultEnabled: true,
      effect:
        'Ausgeschaltet verschwinden Lager, Einkauf und Inventur aus der Oberfläche, und Artikel gelten als nicht bestandsgeführt: Aufträge reservieren nichts, Lieferscheine, Kasse und Rücknahmen buchen keinen Bestand, für die Bestellkanäle ist alles lieferbar. Leihartikel zählt der Verleih weiter über den Bestand. Pfand und Leergutkonten laufen weiter, Leergut geht nicht in den Bestand. Die gespeicherten Bestände bleiben stehen – nach dem Wiedereinschalten sind sie veraltet, dann ist eine Inventur angeraten.',
    }),
    defineFeature({ key: 'lots', label: 'Chargen und MHD', description: 'Chargenpflicht, Mindesthaltbarkeit, FEFO, Sperren und Rückverfolgung.', defaultEnabled: false }),
    // Lagerplätze (docs/kommissionieren/SPEZIFIKATION.md K5): Platzbestand, Umbuchen, Laufweg und Platz beim Kommissionieren.
    // Ohne den Schalter bucht alles wie bisher auf den Standardplatz; ausschalten nur ohne Plätze in Gebrauch (server/bins.ts).
    defineFeature({ key: 'bins', label: 'Lagerplätze', description: 'Bestand je Lagerplatz statt nur je Standort führen.', defaultEnabled: false }),
  ],
  permissions: [
    definePermission({
      key: 'inventory.stock.read',
      label: 'Bestand und Bewegungsjournal lesen',
      group: GROUP,
      description: 'Bestand, Reservierungen, Transit, Lagerplätze und Bewegungen – an den eigenen Standorten.',
    }),
    definePermission({
      key: 'inventory.lot.read',
      label: 'Chargen und MHD lesen',
      group: GROUP,
      description: 'Chargen mit Mindesthaltbarkeit, Status und Rückverfolgung.',
    }),
    definePermission({
      key: 'inventory.lot.block',
      label: 'Chargen sperren, freigeben und zurückrufen',
      group: GROUP,
      description: 'Sperre und Freigabe gelten für die ganze Charge; ein Rückruf sperrt sie an allen Standorten.',
    }),

    definePermission({ key: 'inventory.receipt.read', label: 'Wareneingänge lesen', group: GROUP }),
    definePermission({
      key: 'inventory.receipt.create',
      label: 'Wareneingänge erfassen und buchen',
      group: GROUP,
      description: 'Über der Grenze wartet der Wareneingang auf Freigabe.',
      limits: [{ key: 'maxReceiptCents', label: 'Höchster Warenwert je Wareneingang', unit: 'cents' }],
    }),
    definePermission({
      key: 'inventory.receipt.approve',
      label: 'Wareneingänge freigeben und stornieren',
      group: GROUP,
    }),

    definePermission({
      key: 'inventory.transfer.read',
      label: 'Umlagerungen lesen',
      group: GROUP,
      description: 'Sichtbar sind Umlagerungen, die an einem eigenen Standort beginnen oder enden.',
    }),
    definePermission({
      key: 'inventory.transfer.request',
      label: 'Umlagerungen anfordern',
      group: GROUP,
      description: 'Nachschub für den eigenen Standort anfordern oder eine Umlagerung vom eigenen Standort anlegen.',
    }),
    definePermission({
      key: 'inventory.transfer.ship',
      label: 'Umlagerungen kommissionieren und versenden',
      group: GROUP,
      description: 'Gilt am abgebenden Standort; der Versand bucht die Ware in den Transit.',
    }),
    definePermission({
      key: 'inventory.transfer.receive',
      label: 'Umlagerungen empfangen',
      group: GROUP,
      description: 'Gilt am empfangenden Standort; Fehlmengen bleiben im Transit.',
    }),
    definePermission({
      key: 'inventory.transfer.approve',
      label: 'Umlagerungen freigeben und Transitdifferenzen klären',
      group: GROUP,
      description: 'Gilt am abgebenden Standort: Anforderungen freigeben oder ablehnen, Fehlmengen ausbuchen oder zurückholen.',
    }),

    definePermission({
      key: 'inventory.bin.move',
      label: 'Zwischen Lagerplätzen umbuchen',
      group: GROUP,
      description: 'Ware innerhalb eines Standorts von einem Lagerplatz auf einen anderen buchen und solche Umbuchungen stornieren.',
    }),
    definePermission({ key: 'inventory.adjustment.read', label: 'Korrekturbuchungen lesen', group: GROUP }),
    definePermission({
      key: 'inventory.adjustment.create',
      label: 'Korrekturbuchungen erfassen',
      group: GROUP,
      description: 'Bruch, Verderb, Schwund, Eigenverbrauch, Mehrbestand. Über der Grenze wartet die Korrektur auf Freigabe.',
      limits: [{ key: 'maxAdjustmentCents', label: 'Höchster Wert je Korrekturbuchung', unit: 'cents' }],
    }),
    definePermission({
      key: 'inventory.adjustment.approve',
      label: 'Korrekturbuchungen freigeben',
      group: GROUP,
      description: 'Freigeben oder ablehnen, was über der Grenze der erfassenden Person liegt – nie die eigene Korrektur.',
      limits: [{ key: 'maxAdjustmentCents', label: 'Höchster Wert je Freigabe', unit: 'cents' }],
    }),
  ],
})
